import React, { Component } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'common/i18n';
import AlertSettingModal from 'common/notifications/components/AlertSettingModal/AlertSettingModal';
import { handleTab } from 'common/dom_helpers/keyPressHelpers';
import { ForgeButton } from '@tylertech/forge-react';

import './panel-footer.scss';

const scope = 'shared.site_chrome.notifications';

interface PanelFooterProps {
  clearAllUserNotifications: () => void;
  hasUnreadNotifications: boolean;
  hasUserNotifications: boolean;
  openClearAllUserNotificationsPrompt: boolean;
  showUserNotifications: boolean;
  toggleClearAllUserNotificationsPrompt: (open: boolean) => void;
}

interface PanelFooterState {
  showAlertSettingModal: boolean;
}

class PanelFooter extends Component<PanelFooterProps, PanelFooterState> {
  clearAllButtonRef: React.RefObject<HTMLButtonElement>;

  constructor(props: PanelFooterProps) {
    super(props);
    this.clearAllButtonRef = React.createRef();
    this.state = { showAlertSettingModal: false };
  }

  closeModal = () => {
    this.setState({ showAlertSettingModal: false });
  };

  toggleSettingsModal = () => {
    this.setState({ showAlertSettingModal: !this.state.showAlertSettingModal });
  };

  renderModal = () => {
    const { showAlertSettingModal } = this.state;

    if (showAlertSettingModal) {
      return (
        <AlertSettingModal
          onClose={this.closeModal} />
      );
    }

    return null;
  };

  focusClearAllButton = () => {
    if (this.clearAllButtonRef.current) {
      this.clearAllButtonRef.current.focus();
    }
  };

  renderClearAllNotificationsPrompt = () => {
    const {
      clearAllUserNotifications,
      openClearAllUserNotificationsPrompt,
      toggleClearAllUserNotificationsPrompt
    } = this.props;
    if (openClearAllUserNotificationsPrompt) {
      return (
        (<div className="inline-prompt">
          <p className="heading-text">{I18n.t('clear_all_text', { scope })}</p>
          <p>{I18n.t('clear_all_confirm', { scope })}</p>
          <div className="prompt-buttons-wrapper clearfix">
            <ForgeButton
              className="cancel-button"
              onClick={() => {
                this.focusClearAllButton();
                toggleClearAllUserNotificationsPrompt(false);
              }}
              variant="outlined"
            >
              {I18n.t('clear_all_confirm_no', { scope })}
            </ForgeButton>
            <ForgeButton
              onClick={() => {
                this.focusClearAllButton();
                toggleClearAllUserNotificationsPrompt(false);
                clearAllUserNotifications();
              }}
              variant="raised"
            >
              {I18n.t('clear_all_confirm_yes', { scope })}
            </ForgeButton>
          </div>
        </div>)
      );
    }

    return null;
  };

  focusClosePanelButton = () => {
    const focusClosePanelButton = document.querySelector('.close-notifications-panel-link') as HTMLElement;
    if (focusClosePanelButton) {
      focusClosePanelButton.focus();
    }
  };

  renderSettingsButton = () => {
    const { showUserNotifications } = this.props;

    if (showUserNotifications) {
      return (
        (<ForgeButton
          className="setting-button"
          onClick={this.toggleSettingsModal}
          onKeyPress={handleTab(
            // Sometimes the user can accidentally put focus back on this panel while the AlertSettingModal
            // is still open. In that case, we should let them tab out of this panel and into AlertSettingModal.
            this.state.showAlertSettingModal ? null : this.focusClosePanelButton,
            this.focusClearAllButton,
            true)}
          variant="raised"
        >{I18n.t('setting', { scope })}</ForgeButton>)
      );
    }

    return null;
  };

  renderFooter = () => {
    const {
      hasUserNotifications,
      openClearAllUserNotificationsPrompt,
      toggleClearAllUserNotificationsPrompt
    } = this.props;

    return (
      (<div className="buttons-wrapper clearfix">
        <ForgeButton
          ref={this.clearAllButtonRef}
          className="clear-all-button"
          onClick={() => toggleClearAllUserNotificationsPrompt(hasUserNotifications && !openClearAllUserNotificationsPrompt)}
          variant="outlined"
          disabled={!hasUserNotifications}>
          {I18n.t('clear_all_text', { scope })}
        </ForgeButton>
        {this.renderClearAllNotificationsPrompt()}
        {this.renderSettingsButton()}
      </div>)
    );
  };

  render() {
    return (
      <div className="footer-bar">
        {this.renderFooter()}
        {this.renderModal()}
      </div>
    );
  }
}

const mapStateToProps = (state: { configurations: { showUserNotifications: any; }; }) => {
  return ({
    showUserNotifications: state.configurations.showUserNotifications
  });
}

export default connect(mapStateToProps)(PanelFooter);
